import type { z } from 'zod';
import { CSRF_HEADER, errorBodySchema } from '../../../shared/api/common.ts';

/** Error de la API o de la red con un mensaje ya en castellano, listo para mostrar. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterSeconds: number | undefined;

  constructor(status: number, code: string, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Los datos enviados no son válidos.',
  401: 'La sesión ha caducado. Vuelve a entrar.',
  403: 'No tienes permiso para hacer esto.',
  404: 'No hemos encontrado lo que buscabas.',
  409: 'Esa operación entra en conflicto con el estado actual.',
  413: 'La petición es demasiado grande.',
  429: 'Demasiadas peticiones: espera un momento y vuelve a probar.',
};

function fallbackMessage(status: number): string {
  return (
    FALLBACK_MESSAGES[status] ??
    (status >= 500
      ? 'El servidor no ha podido completar la operación. Inténtalo de nuevo en unos minutos.'
      : 'No se ha podido completar la operación.')
  );
}

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

/** Token CSRF de la sesión actual: se reenvía en los métodos que modifican datos. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Función a la que se avisa cuando una petición (que no es el login) recibe un 401. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Esquema zod de la respuesta; si no encaja, se trata como error del servidor. */
  schema: z.ZodType<T>;
  signal?: AbortSignal;
  /** El login, `me` y el cierre de sesión gestionan ellos mismos el 401. */
  skipUnauthorizedHandler?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions<T>): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (!SAFE_METHODS.has(method) && csrfToken) headers[CSRF_HEADER] = csrfToken;

  let res: Response;
  try {
    const init: RequestInit = { method, headers, credentials: 'same-origin' };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;
    res = await fetch(path, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Sin conexión con el servidor. Comprueba la red y reintenta.');
  }

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }

  if (!res.ok) {
    if (res.status === 401 && !options.skipUnauthorizedHandler) unauthorizedHandler?.();
    const parsed = errorBodySchema.safeParse(json);
    const retry = Number(res.headers.get('retry-after'));
    throw new ApiError(
      res.status,
      parsed.success ? parsed.data.error : 'http_error',
      parsed.success && parsed.data.message ? parsed.data.message : fallbackMessage(res.status),
      Number.isFinite(retry) && retry > 0 ? retry : undefined,
    );
  }

  const result = options.schema.safeParse(json);
  if (!result.success) {
    throw new ApiError(res.status, 'bad_response', 'La respuesta del servidor no es la esperada.');
  }
  return result.data;
}
